import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import {
  toPairs,
  reduce,
  camelCase,
  snakeCase,
  isPlainObject,
  isArray,
} from "lodash";
import env from "config/env";

const convertCase = (data: any, converter: (str?: string) => string): any => {
  if (isPlainObject(data)) {
    const accumulator: { [s: string]: any } = {};
    return reduce(
      data,
      (result, value, key) => {
        result[converter(key)] = convertCase(value, converter);
        return result;
      },
      accumulator
    );
  }

  if (isArray(data)) {
    return data.map((d) => convertCase(d, converter));
  }

  return data;
};

export default class Request {
  private errorHandler?: (error: AxiosError) => AxiosResponse | undefined;

  private config: AxiosRequestConfig = {
    baseURL: env.api.baseUrl,
    headers: {
      "Content-Type": "application/json",
      Authorization: "",
    },
    transformResponse: (data: any) => convertCase(JSON.parse(data), camelCase),
    transformRequest: (data: any) =>
      JSON.stringify(convertCase(data, snakeCase)),
  };

  public setConfig(config: AxiosRequestConfig) {
    this.config = { ...this.config, ...config };
  }

  public setErrorHandler(
    errorHandler: <T>(error: AxiosError<T>) => AxiosResponse<T> | undefined
  ) {
    this.errorHandler = errorHandler;
  }

  public get<R>(
    path: string,
    params?: { [key: string]: any },
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<R> | undefined> {
    const query = toPairs(convertCase(params, snakeCase) || {})
      .map((pairs) => pairs.join("="))
      .join("&");
    return axios
      .get<R>(query ? `${path}?${query}` : path, { ...this.config, ...config })
      .catch(this.errorHandler);
  }

  public async post<R>(
    path: string,
    params?: { [key: string]: any },
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<R> | undefined> {
    return axios
      .post<R>(path, params, { ...this.config, ...config })
      .catch(this.errorHandler);
  }
}
